import { Modal } from '@lightspeed/design-system-react'
import uuid from '@vend/vend-uuid'
import { reactular } from '@vendhq/reactular'
import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { ApiRetailerPaymentType } from '@vend/types'
import { vn } from 'vend-number'
import { useTrackPaymentDuration } from '../../hooks/useTrackPaymentDuration'
import { Transaction } from './queries/Transaction'
import { SwiperPaymentModalContent } from './components/SwiperPaymentModalContent/SwiperPaymentModalContent'
import { useSwiperPaymentReducer } from './hooks/use-swiper-payment-reducer/useSwiperPaymentReducer'

interface Props {
  closeModal: (val?: Transaction) => void
  registerId: string
  retailerPaymentType: ApiRetailerPaymentType
  amount: number
  currency: string
}

export const SwiperPaymentModal: FC<Props> = ({
  closeModal,
  registerId,
  retailerPaymentType,
  amount,
  currency,
}) => {
  const registerSalePaymentId = useMemo(() => uuid(), [])
  const isRefund = useMemo(() => vn(amount).isNegative(), [amount])

  const [state, dispatch] = useSwiperPaymentReducer()

  const trackEnd = useTrackPaymentDuration(
    'Swiper payment',
    registerId,
    amount,
    retailerPaymentType.type_id,
    {
      isReact: true,
      isRefund,
      transaction: registerSalePaymentId,
    }
  )

  // override the modal closing function and add tracking
  const closeModalWithTracking = useCallback(
    (transaction?: Transaction): void => {
      trackEnd({ transaction })
      closeModal(transaction)
    },
    [trackEnd, closeModal]
  )

  // Close the modal with the transaction if the status is "done"
  useEffect(() => {
    if (state.status === 'done') {
      closeModalWithTracking(state.transaction)
    }
  }, [state, closeModalWithTracking])

  return (
    <Modal
      onClose={() => closeModalWithTracking()}
      dismissable={state.status === 'error'}
      focusTrapActive={true}
      size="small"
    >
      <SwiperPaymentModalContent
        isRefund={isRefund}
        state={state}
        registerID={registerId}
        registerSalePaymentID={registerSalePaymentId}
        retailerPaymentType={retailerPaymentType}
        amount={amount}
        currency={currency}
        dispatch={dispatch}
      />
    </Modal>
  )
}

export const SwiperPaymentModalComponent = reactular(
  SwiperPaymentModal,
  ['closeModal', 'amount', 'currency', 'registerId', 'retailerPaymentType'],
  'reactWrapper'
)
